// PatrollingLocationApi.js - API utilities for Patrolling Location operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for Patrolling Location API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.error || errorData.body?.error || 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_patrolling_location',
        `Patrolling Location API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[PatrollingLocationApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_patrolling_location',
      `Patrolling Location API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[PatrollingLocationApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Patrolling Location Management.
 */
export const patrollingLocationApi = {
  /**
   * Fetches map data including all employees' locations in a patrol.
   * GET /patrol/{patrol_id}/map
   * 
   * Lambda Function: innoworx-get-patrol-map-data (or rakshak-innoworx-get-patrol-map-data)
   * 
   * @param {string} patrolId - The patrol ID to fetch map data for.
   * @returns {Promise<Object>} - Response object containing:
   *   - patroller_locations: Array of all employees' locations in the patrol
   *     [
   *       {
   *         user_id: "string",
   *         latitude: number,
   *         longitude: number,
   *         timestamp: "ISO8601"
   *       }
   *     ]
   *   - vehicle_location: For vehicle patrols, aggregated location (average of all user locations)
   *   - QR locations, route, and completed QRs
   * @throws {Error} - Throws an error if the API request fails.
   * 
   * @example
   * const mapData = await patrollingLocationApi.getPatrolMapData('patrol-123');
   * console.log('Patroller locations:', mapData.patroller_locations);
   */
  getPatrolMapData: async (patrolId) => {
    try {
      if (!patrolId) {
        throw new Error('patrolId is required');
      }

      console.log('Patrolling Location API: Fetching map data for patrol', patrolId);

      const response = await makeApiRequest(`/patrol/${patrolId}/map`, 'GET');
      const data = await response.json();

      console.log('Patrolling Location API: Successfully fetched patrol map data', data);
      return data;
    } catch (error) {
      console.error('Patrolling Location API: Error fetching patrol map data', error);
      throw error;
    }
  },

  /**
   * Fetches the current active patrol for the authenticated employee, including their location.
   * GET /patrol/current
   * 
   * Lambda Function: innoworx-get-current-patrol (or rakshak-innoworx-get-current-patrol)
   * 
   * @returns {Promise<Object>} - Response object containing:
   *   - location: Latest location for the authenticated employee
   *     {
   *       latitude: number,
   *       longitude: number,
   *       timestamp: "ISO8601",
   *       accuracy: number
   *     }
   *   - Patrol details, attendance record, and QR progress
   * @throws {Error} - Throws an error if the API request fails.
   * 
   * @example
   * const currentPatrol = await patrollingLocationApi.getCurrentPatrol();
   * console.log('Current location:', currentPatrol.location);
   */
  getCurrentPatrol: async () => {
    try {
      console.log('Patrolling Location API: Fetching current patrol for authenticated employee');

      const response = await makeApiRequest('/patrol/current', 'GET');
      const data = await response.json();

      console.log('Patrolling Location API: Successfully fetched current patrol', data);
      return data;
    } catch (error) {
      console.error('Patrolling Location API: Error fetching current patrol', error);
      throw error;
    }
  },
};

